import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Loader2, ArrowUpRight } from "lucide-react";
import { WaButton } from "./Buttons";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, ZONA, SERVICES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
    { icon: Phone, label: "Telefono", value: PHONE_DISPLAY, href: `tel:${PHONE_TEL}`, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email" },
    { icon: MapPin, label: "Zone servite", value: ZONA, testid: "contact-zone" },
];

export default function Contact() {
    const [form, setForm] = useState({ nome: "", telefono: "", servizio: SERVICES[0].title, messaggio: "" });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nome.trim() || !form.telefono.trim()) {
            toast.error("Inserisci nome e numero di telefono.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/quote`, form);
            toast.success("Richiesta inviata! Ti ricontattiamo al più presto.");
            setForm({ nome: "", telefono: "", servizio: SERVICES[0].title, messaggio: "" });
        } catch (err) {
            toast.error("Invio non riuscito. Scrivici pure su WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-bitume/60 border border-white/10 px-4 py-3.5 text-sm text-cloud placeholder:text-ash/50 outline-none transition-colors duration-300 focus:border-aqua";

    return (
        <section id="contatti" className="relative py-24 md:py-32" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
                <div>
                    <p className="eyebrow mb-4 flex items-center gap-3">
                        <span className="inline-block h-px w-8 bg-aqua" />
                        Contatti
                    </p>
                    <h2 className="font-display font-bold tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl text-cloud">
                        Parliamone del tuo <span className="text-ambra">lastrico.</span>
                    </h2>
                    <p className="mt-5 text-ash leading-relaxed max-w-md text-base sm:text-lg">
                        Sopralluogo e preventivo senza impegno: raccontaci il problema, ti diremo come
                        lo risolviamo.
                    </p>

                    <div className="mt-9 flex flex-col gap-3 max-w-md">
                        <WaButton className="w-full" />
                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="contact-cta-call"
                            className="inline-flex items-center justify-center gap-2.5 border border-white/15 text-cloud font-semibold tracking-tight px-6 py-4 frame-clipped-sm transition-all duration-300 hover:border-aqua hover:text-aqua"
                        >
                            <Phone className="w-5 h-5" strokeWidth={2.2} />
                            Chiama ora · {PHONE_DISPLAY}
                        </a>
                    </div>

                    <ul className="mt-10 space-y-4">
                        {INFO.map((row) => (
                            <li key={row.label} className="flex items-center gap-4" data-testid={row.testid}>
                                <span className="w-10 h-10 shrink-0 border border-white/10 bg-panel flex items-center justify-center">
                                    <row.icon className="w-4 h-4 text-aqua" strokeWidth={1.8} />
                                </span>
                                <div>
                                    <span className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-ash/70 block">
                                        {row.label}
                                    </span>
                                    {row.href ? (
                                        <a href={row.href} className="text-sm text-cloud hover:text-aqua transition-colors">
                                            {row.value}
                                        </a>
                                    ) : (
                                        <span className="text-sm text-cloud">{row.value}</span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="quote-form"
                    className="bg-panel border border-white/10 p-7 md:p-10 h-fit frame-clipped"
                >
                    <h3 className="font-display font-semibold tracking-tight text-2xl text-cloud flex items-center gap-2">
                        Richiedi un preventivo
                        <ArrowUpRight className="w-5 h-5 text-ambra" />
                    </h3>
                    <p className="mt-2 text-sm text-ash">Risposta rapida, senza impegno.</p>

                    <div className="mt-7 space-y-4">
                        <div>
                            <label htmlFor="q-nome" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">
                                Nome e cognome *
                            </label>
                            <input
                                id="q-nome"
                                data-testid="quote-input-nome"
                                className={inputCls}
                                placeholder="Mario Rossi"
                                value={form.nome}
                                onChange={set("nome")}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="q-tel" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">
                                Telefono *
                            </label>
                            <input
                                id="q-tel"
                                data-testid="quote-input-telefono"
                                className={inputCls}
                                placeholder="+39 ..."
                                type="tel"
                                value={form.telefono}
                                onChange={set("telefono")}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="q-serv" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">
                                Tipo di intervento
                            </label>
                            <select
                                id="q-serv"
                                data-testid="quote-select-servizio"
                                className={`${inputCls} appearance-none cursor-pointer`}
                                value={form.servizio}
                                onChange={set("servizio")}
                            >
                                {SERVICES.map((s) => (
                                    <option key={s.n} value={s.title} className="bg-panel">
                                        {s.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="q-msg" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">
                                Messaggio
                            </label>
                            <textarea
                                id="q-msg"
                                data-testid="quote-input-messaggio"
                                className={`${inputCls} min-h-[110px] resize-y`}
                                placeholder="Descrivi brevemente il problema (es. infiltrazione sotto il terrazzo)..."
                                value={form.messaggio}
                                onChange={set("messaggio")}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="quote-submit-button"
                            className="w-full inline-flex items-center justify-center gap-2.5 bg-ambra text-bitume font-semibold tracking-tight px-6 py-4 frame-clipped-sm transition-all duration-300 hover:brightness-110 disabled:opacity-60"
                        >
                            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia la richiesta"}
                        </button>
                        <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-ash/60 text-center">
                            Oppure scrivici direttamente su WhatsApp
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
}
